<template>
  <section class="rounded-[24px] border border-[#eee4da] bg-white p-4 shadow-sm sm:p-6">
    <div class="flex flex-col gap-3 border-b border-[#eee4da] pb-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <RouterLink to="/dashboard/settings" class="inline-flex items-center gap-2 text-sm font-semibold text-brand">
          <i class="ri-arrow-left-line"></i>
          Settings
        </RouterLink>
        <h2 class="mt-3 text-[1.65rem] font-semibold leading-tight text-ink">{{ page.title }}</h2>
        <p class="mt-1 text-sm text-muted">{{ page.subtitle }}</p>
      </div>
      <span class="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fbf1e6] text-2xl text-brand">
        <i :class="page.icon"></i>
      </span>
    </div>

    <div class="mt-6 grid gap-4">
      <article v-for="section in page.sections" :key="section.heading" class="rounded-2xl border border-[#eadfd4] bg-[#fffaf5] p-4">
        <h3 class="text-base font-semibold text-ink">{{ section.heading }}</h3>
        <p class="mt-2 text-sm leading-6 text-muted">{{ section.body }}</p>
      </article>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

const route = useRoute()

const pages = {
  about: {
    title: 'About Invi8',
    subtitle: 'A workspace for vendors to manage listings, leads, and booking-ready information.',
    icon: 'ri-information-line',
    sections: [
      { heading: 'What Invi8 does', body: 'Invi8 helps venue and service vendors present accurate business information, pricing, policies, galleries, and availability in one place.' },
      { heading: 'Vendor tools', body: 'Use the dashboard to create businesses, venues, service vendor profiles, packages, spaces, and listing previews for customer discovery.' },
      { heading: 'Our focus', body: 'The platform is designed around clear information, faster enquiry handling, and more reliable event planning workflows.' }
    ]
  },
  support: {
    title: 'Support',
    subtitle: 'Get help with profile setup, listings, documents, and account access.',
    icon: 'ri-customer-service-2-line',
    sections: [
      { heading: 'Account and login help', body: 'If you cannot access your vendor account, use the password reset flow from the login page or contact the Invi8 support team.' },
      { heading: 'Listing help', body: 'For venue, package, gallery, policy, or pricing issues, include the business name, venue name, and a short description of the problem.' },
      { heading: 'Document help', body: 'If a compliance document is rejected or cannot be uploaded, verify the file type and size, then try again from the Documents tab.' }
    ]
  },
  faqs: {
    title: 'FAQs',
    subtitle: 'Common answers for vendor account and listing management.',
    icon: 'ri-question-line',
    sections: [
      { heading: 'Can I edit a listing after publishing?', body: 'Yes. You can update business, venue, package, gallery, availability, and policy details from the dashboard.' },
      { heading: 'Why are documents required?', body: 'Documents help verify business identity and keep marketplace listings trustworthy for customers.' },
      { heading: 'How do customers see pricing?', body: 'Pricing appears from the package, space, or menu pricing information you add while setting up a venue or service vendor.' }
    ]
  },
  terms: {
    title: 'Terms of Use',
    subtitle: 'Operational terms for using the Invi8 vendor dashboard.',
    icon: 'ri-file-text-line',
    sections: [
      { heading: 'Accurate information', body: 'Vendors are responsible for keeping business, contact, pricing, policy, and availability information accurate and up to date.' },
      { heading: 'Permitted use', body: 'The dashboard should be used only for managing legitimate vendor listings, enquiries, and event-related services.' },
      { heading: 'Content responsibility', body: 'Uploaded images, documents, descriptions, and policies must belong to your business or be content you have permission to use.' }
    ]
  },
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How vendor information is used inside the dashboard and marketplace.',
    icon: 'ri-shield-check-line',
    sections: [
      { heading: 'Profile information', body: 'We use vendor profile and business details to operate your dashboard, verify listings, and show public listing information where applicable.' },
      { heading: 'Documents', body: 'Business documents are used for verification and administrative review. They are not intended to be shown publicly to customers.' },
      { heading: 'Account security', body: 'Keep your password private and update it regularly. If you suspect unauthorized access, change your password immediately.' }
    ]
  }
}

const page = computed(() => pages[route.meta.contentKey] || pages.about)
</script>
